// Copyright (c) 2026 Yunus YILDIZ — SPDX-License-Identifier: BUSL-1.1

import { Search } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useAuth } from '@/lib/auth/useAuth'
import { useNavigate } from 'react-router-dom'
import { Avatar } from '@/components/ui/Avatar'
import { Button } from '@/components/ui/Button'
import { CommandPalette } from '@/components/ui/CommandPalette'
import { ThemeToggle } from '@/components/ui/ThemeToggle'
import { WorkspaceSwitcher } from '@/components/layout/WorkspaceSwitcher'
import { NotificationCenter } from '@/components/layout/NotificationCenter'
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from '@/components/ui/DropdownMenu'
import { ROUTES } from '@/constants/routes'
import { LogOut, User, Settings } from 'lucide-react'

export function TopBar() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [cmdOpen, setCmdOpen] = useState(false)

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setCmdOpen((o) => !o)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  return (
    <>
      <header className="relative flex h-12 items-center border-b border-line bg-canvas px-4 duration-200">

        {/* Search trigger — desktop only, centered */}
        <div className="hidden md:block absolute left-1/2 -translate-x-1/2">
          <div className="rounded-full bg-gradient-to-r from-brand to-accent p-px">
            <button
              onClick={() => setCmdOpen(true)}
              className="flex w-72 items-center gap-2 rounded-full bg-canvas px-4 py-1.5 text-sm text-ink-muted hover:text-ink transition-colors"
            >
              <Search className="h-3.5 w-3.5 shrink-0" />
              <span className="flex-1 text-left">Search…</span>
              <kbd className="flex items-center gap-0.5 rounded-sm bg-surface px-1.5 py-0.5 font-mono text-[10px] text-ink">
                <span>⌘</span><span>K</span>
              </kbd>
            </button>
          </div>
        </div>

        <div className="ml-auto flex items-center gap-2">
          <WorkspaceSwitcher />
          {/* Search icon — mobile only */}
          <Button
            variant="ghost"
            size="icon-sm"
            className="md:hidden"
            onClick={() => setCmdOpen(true)}
            aria-label="Search"
          >
            <Search className="h-4 w-4" />
          </Button>

          {/* Theme toggle */}
          <ThemeToggle />

          <NotificationCenter />

          {/* User menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="ml-1 flex items-center gap-2 rounded-md p-1 hover:bg-surface transition-colors">
                <Avatar name={user?.name} size="sm" />
                <span className="hidden text-sm font-medium text-ink md:block">{user?.name}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem icon={<User className="h-4 w-4" />} onSelect={() => navigate(ROUTES.SETTINGS_PROFILE)}>
                Profile
              </DropdownMenuItem>
              <DropdownMenuItem icon={<Settings className="h-4 w-4" />} onSelect={() => navigate(ROUTES.SETTINGS)}>
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem icon={<LogOut className="h-4 w-4" />} destructive onSelect={() => { void logout() }}>
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <CommandPalette open={cmdOpen} onOpenChange={setCmdOpen} />
    </>
  )
}
